import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { SupplierWrite, InputMaxLength } from "types"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import useStorages from "hooks/useStorages"
import useStorageLimits from "hooks/useStorageLimits"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import Button from "components/inputs/button"
import useLayout from "hooks/useLayout"
import { logger } from "utils/secureLogger"
import ProcessingButton from "components/inputs/processingButton"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import useSuppliers from "hooks/useSuppliers"

interface CreateSupplierFormProps {
    storageId: string
    onSuccess?: (res: any) => void
}   

const CreateSupplierForm: React.FC<CreateSupplierFormProps> = ({ storageId, onSuccess }) => {

    const { t } = useTranslation()

    // React hooks.

    // Component state.
    const [supplierId, setSupplierId] = useState<number>(1)
    const [name, setName] = useState(``)
    const [email, setEmail] = useState(``)
    const [phone, setPhone] = useState(``)
    const [notes, setNotes] = useState(``)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Layout hooks.
    const { addToast } = useLayout()

    const {
        lastAddedSupplierId
    } = useStorages({ storageId })

    const { createSupplierAsync, isCreating } = useSuppliers({ storageId })
    
    const { storage } = useStorages({ storageId })
    
    // Check account limits
    const storagePlan = storage?.settings?.storagePlan
    const { canUseSuppliers } = useStorageLimits({ storagePlan })

    // Set initial supplier ID
    useEffect(() => {
        setSupplierId(lastAddedSupplierId + 1 || 1)
    }, [lastAddedSupplierId])

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent) => {
        logger.info('Create supplier form submitted successfully')
        event.preventDefault()
        setErrors([])
        
        if (!storageId) {
            setErrors([t('errorNoStorageId')])
            return
        }
        
        // Check if user can use suppliers feature
        if (!canUseSuppliers) {
            setErrors([t('errorFeatureRequiresProPlan')])
            addToast({ text: t('errorFeatureRequiresProPlan'), colorScheme: 'red' })
            return
        }

        try {
            const supplierData: SupplierWrite = {
                _version: 1,
                ids: {
                    storageId,
                },
                supplierId: supplierId,
                name,
                notes,
                contactInformation: {
                    email,
                    phone,
                },
            }
            console.log("supplierData: ", supplierData)
            const result = await createSupplierAsync(supplierData) as { id?: string; path?: string }
            console.log("result: ", result)
            const createdSupplierId = result.id || result.path?.split('/').pop()
            logger.info('Supplier created successfully', { supplierData, supplierId: createdSupplierId })
            addToast({ text: t('toastSupplierCreated'), colorScheme: 'green' })
            if (onSuccess) onSuccess(createdSupplierId)
        } catch (error: unknown) {
            logger.error('Failed to create supplier', error)
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
        }
    }

    // Handle ID change.
    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 1
        setSupplierId(numericValue)
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle phone change.
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value)
    }

    // Handle notes change.
    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value)
    }

    // Render form.
    return (
        <Form onSubmit={handleSubmit}>
            <Section>
                <TextInput
                    label={t('labelSupplierId')}
                    value={supplierId.toString()}
                    onChange={handleIdChange}
                    required
                    maxLength={InputMaxLength.short}
                />
                <TextInput
                    label={t('labelSupplierName')}
                    value={name}
                    onChange={handleNameChange}
                    required
                    maxLength={InputMaxLength.short}
                />
                <TextInput
                    label={t('labelSupplierEmail')}
                    value={email}
                    onChange={handleEmailChange}
                    maxLength={InputMaxLength.medium}
                />
                <TextInput
                    label={t('labelSupplierPhone')}
                    value={phone}
                    onChange={handlePhoneChange}
                    maxLength={InputMaxLength.short}
                />
                <TextAreaInput
                    label={t('labelSupplierNotes')}
                    value={notes}
                    onChange={handleNotesChange}
                    maxLength={InputMaxLength.extraLong}
                />
            </Section>
            
            <Section>
                <div className="input-holder">
                    {isCreating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary" disabled={!name.trim() || !canUseSuppliers}>
                            <AddIcon />
                            <span>{t('buttonCreateSupplier')}</span>
                        </Button>
                    )}
                </div>
            </Section>
            
            <FormErrors errors={errors} />
        </Form>
    )
}

export default CreateSupplierForm
